import { Progress, Tag, Tooltip } from 'antd';
import type { ReactNode } from 'react';

import {
  ProjectHealth,
  ProjectPriority,
  ProjectStatus,
  projectHealthNames,
  projectPriorityNames,
  projectStatusNames,
} from '../../api/projects';
import type {
  ProjectHealthValue,
  ProjectPriorityValue,
  ProjectStatusValue,
} from '../../api/projects';

/**
 * Phần hiển thị dùng chung của module dự án.
 *
 * Gom về một tệp thay vì viết tại từng trang, vì cùng một trạng thái sẽ xuất hiện ở danh sách, ở
 * bảng Kanban và ở trang chi tiết. Ba chỗ tô ba màu khác nhau cho cùng một trạng thái là thứ làm
 * người dùng mất lòng tin vào bảng màu, và khi đó màu sắc không còn truyền được thông tin gì.
 */

/** Màu của từng trạng thái dự án. */
const statusColors: Readonly<Record<ProjectStatusValue, string>> = {
  [ProjectStatus.Draft]: 'default',
  [ProjectStatus.InProgress]: 'processing',
  [ProjectStatus.OnHold]: 'warning',
  [ProjectStatus.Installing]: 'cyan',
  [ProjectStatus.PendingAcceptance]: 'gold',
  [ProjectStatus.UnderWarranty]: 'blue',
  [ProjectStatus.Closed]: 'success',
  [ProjectStatus.Cancelled]: 'error',
};

/**
 * Màu của từng mức sức khỏe.
 *
 * Mức tốt cố ý dùng màu nhạt: nếu tô xanh đậm thì cả bảng sẽ rực lên màu xanh và những dòng có vấn
 * đề không còn nổi bật. Dấu hiệu chỉ có tác dụng khi nó hiếm.
 */
const healthColors: Readonly<Record<ProjectHealthValue, string>> = {
  [ProjectHealth.Good]: 'default',
  [ProjectHealth.NeedsAttention]: 'gold',
  [ProjectHealth.AtRisk]: 'orange',
  [ProjectHealth.Critical]: 'red',
};

/** Màu của từng mức ưu tiên. */
const priorityColors: Readonly<Record<ProjectPriorityValue, string>> = {
  [ProjectPriority.Critical]: 'red',
  [ProjectPriority.High]: 'orange',
  [ProjectPriority.Normal]: 'default',
  [ProjectPriority.Low]: 'default',
};

/** Tên tiếng Việt của một trạng thái, dùng khi cần chuỗi thuần thay vì nhãn có màu. */
export function projectStatusLabel(status: ProjectStatusValue): string {
  return projectStatusNames[status];
}

/** Nhãn trạng thái dự án. */
export function statusTag(status: ProjectStatusValue): ReactNode {
  return <Tag color={statusColors[status]}>{projectStatusNames[status]}</Tag>;
}

/** Nhãn tình trạng sức khỏe, kèm lý do khi có. */
export function healthTag(health: ProjectHealthValue, reason: string | null): ReactNode {
  const tag = <Tag color={healthColors[health]}>{projectHealthNames[health]}</Tag>;

  if (reason === null || reason.trim() === '') {
    return tag;
  }

  return <Tooltip title={reason}>{tag}</Tooltip>;
}

/** Nhãn mức độ ưu tiên. */
export function priorityTag(priority: ProjectPriorityValue): ReactNode {
  return <Tag color={priorityColors[priority]}>{projectPriorityNames[priority]}</Tag>;
}

/**
 * Thanh tiến độ so sánh kế hoạch với thực tế.
 *
 * Hiển thị con số thực tế nhưng tô màu theo **chênh lệch** với kế hoạch. Con số thực tế đứng một
 * mình không nói được gì: sáu mươi phần trăm là tốt hay tệ phụ thuộc hoàn toàn vào việc kế hoạch nói
 * bao nhiêu tại thời điểm này.
 */
export function progressBar(plannedPercent: number, actualPercent: number): ReactNode {
  const gap = actualPercent - plannedPercent;

  const status = gap < -tolerablePercentGap ? 'exception' : 'normal';

  const roundedPlanned = Math.round(plannedPercent).toString();

  const hint =
    gap < -tolerablePercentGap
      ? `Chậm ${Math.round(-gap).toString()}% so với kế hoạch ${roundedPlanned}%`
      : `Kế hoạch ${roundedPlanned}%`;

  return (
    <Tooltip title={hint}>
      <Progress percent={Math.round(actualPercent)} size="small" status={status} />
    </Tooltip>
  );
}

/**
 * Mức chênh lệch còn chấp nhận được giữa tiến độ thực tế và kế hoạch, tính theo phần trăm.
 *
 * Năm phần trăm là khoảng dao động bình thường của một dự án đang chạy đúng hướng. Đặt bằng không sẽ
 * làm gần như mọi dự án bị tô đỏ vào giữa tuần, và một dấu hiệu luôn bật thì không còn là dấu hiệu.
 */
const tolerablePercentGap = 5;

/** Các lựa chọn trạng thái cho ô lọc và ô chọn. */
export const statusOptions = Object.entries(projectStatusNames).map(([value, label]) => ({
  value: Number(value) as ProjectStatusValue,
  label,
}));

/** Các lựa chọn mức ưu tiên. */
export const priorityOptions = Object.entries(projectPriorityNames).map(([value, label]) => ({
  value: Number(value) as ProjectPriorityValue,
  label,
}));

/** Các lựa chọn mức sức khỏe. */
export const healthOptions = Object.entries(projectHealthNames).map(([value, label]) => ({
  value: Number(value) as ProjectHealthValue,
  label,
}));

/**
 * Định dạng một ngày do máy chủ trả về sang dạng ngày tháng năm quen thuộc.
 *
 * Máy chủ trả về dạng năm-tháng-ngày, còn người Việt đọc ngày trước. Cắt chuỗi thay vì dựng đối
 * tượng ngày: những giá trị này là ngày thuần, không có giờ và không gắn múi giờ, nên dựng đối tượng
 * ngày sẽ mở đường cho việc lệch một ngày khi trình duyệt ở múi giờ khác.
 */
export function formatDate(value: string | null): string {
  if (value === null || value.length < 10) {
    return '—';
  }

  const parts = value.slice(0, 10).split('-');

  // Ba phần chắc chắn có vì chuỗi đã được kiểm độ dài ở trên, nhưng phép cắt chuỗi không nói được
  // điều đó với trình kiểm kiểu — nên vẫn phải có giá trị thay thế thay vì khẳng định suông.
  const [year = '', month = '', day = ''] = parts;

  return `${day}/${month}/${year}`;
}

/**
 * Định dạng một mốc thời gian do máy chủ trả về sang ngày giờ đọc được.
 *
 * Khác <see cref="formatDate"/>: giá trị ở đây là mốc thời gian <b>có gắn múi giờ</b>, nên dựng đối
 * tượng ngày là đúng — nó đổi sang giờ của máy người dùng thay vì hiển thị nguyên chuỗi của máy chủ.
 * Với nhật ký báo tiến độ thì giờ là phần quan trọng: hai lần báo cách nhau vài phút và hai lần báo
 * cách nhau ba tuần nói hai điều hoàn toàn khác nhau.
 */
export function formatDateTime(value: string | null): string {
  if (value === null) {
    return '—';
  }

  const moment = new Date(value);

  if (Number.isNaN(moment.getTime())) {
    return '—';
  }

  return moment.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
